const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Obtener entregas de lencería alistadas
 */
export const fetchEntregasLenceriaAlistadas = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-lenceria/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'alistado',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería');
    }

    const result = await response.json();
    
    // La respuesta puede venir en diferentes formatos:
    // Formato 1: { data: { entregas: [...], total: n } }
    // Formato 2: { entregas: [...], total: n }
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaAlistadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería vertical alistadas
 */
export const fetchEntregasLenceriaVerticalAlistadas = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-lenceria-vertical/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'alistado',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería vertical');
    }

    const result = await response.json();
    
    // El backend puede devolver en diferentes formatos:
    // 1. Array directo: [...]
    // 2. Objeto con data: { data: [...], total: n }
    // 3. Objeto con entregas: { entregas: [...], total: n }
    let entregas = [];
    let total = 0;
    
    if (Array.isArray(result)) {
      // Formato 1: Array directo
      entregas = result;
      total = result.length;
    } else if (result.data) {
      // Formato 2: { data: [...] } o { data: { entregas: [...] } }
      if (Array.isArray(result.data)) {
        entregas = result.data;
        total = result.total || result.data.length;
      } else {
        entregas = result.data.entregas || [];
        total = result.data.total || result.total || entregas.length;
      }
    } else if (result.entregas) {
      // Formato 3: { entregas: [...] }
      entregas = result.entregas;
      total = result.total || result.totalRecords || entregas.length;
    }
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaVerticalAlistadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de insumos alistadas
 */
export const fetchEntregasInsumosAlistadas = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-insumos/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'alistado',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de insumos');
    }

    const result = await response.json();
    
    // La respuesta puede venir en diferentes formatos:
    // Formato 1: { data: { entregas: [...], total: n } }
    // Formato 2: { entregas: [...], total: n }
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasInsumosAlistadas:', error);
    throw error;
  }
};

/**
 * Obtener todas las entregas alistadas (lencería horizontal + vertical + insumos)
 */
export const fetchEntregasAlistadas = async () => {
  try {
    // Obtener entregas de lencería horizontal
    const lenceriaResponse = await fetchEntregasLenceriaAlistadas();

    // Obtener entregas de lencería vertical
    const lenceriaVerticalResponse = await fetchEntregasLenceriaVerticalAlistadas();

    // Obtener entregas de insumos
    const insumosResponse = await fetchEntregasInsumosAlistadas();

    // Agregar tipo a cada entrega
    const lenceriaConTipo = (lenceriaResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'horizontal'
    }));

    const lenceriaVerticalConTipo = (lenceriaVerticalResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'vertical'
    }));

    const insumosConTipo = (insumosResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'insumos',
    }));

    // Combinar todas las listas
    const todasEntregas = [...lenceriaConTipo, ...lenceriaVerticalConTipo, ...insumosConTipo];

    // Ordenar por fecha de alistamiento (más recientes primero)
    todasEntregas.sort((a, b) => {
      return new Date(b.fecha_alistamiento || b.createdAt) - new Date(a.fecha_alistamiento || a.createdAt);
    });

    return {
      entregas: todasEntregas,
      total: todasEntregas.length,
      totalLenceria: lenceriaConTipo.length,
      totalLenceriaVertical: lenceriaVerticalConTipo.length,
      totalInsumos: insumosConTipo.length,
    };
  } catch (error) {
    console.error('Error en fetchEntregasAlistadas:', error);
    throw error;
  }
};

/**
 * Marcar entregas como "en camino"
 */
export const marcarEntregasEnCamino = async (entregas, conductor) => {
  try {
    const results = [];
    const errors = [];

    // Procesar cada entrega
    for (const entrega of entregas) {
      try {
        let endpoint;
        if (entrega.tipo === 'lenceria' && entrega.logistica === 'vertical') {
          endpoint = `${API_URL}/entregas-lenceria-vertical/marcar-en-camino`;
        } else if (entrega.tipo === 'lenceria') {
          endpoint = `${API_URL}/entregas-lenceria/marcar-en-camino`;
        } else {
          endpoint = `${API_URL}/entregas-insumos/marcar-en-camino`;
        }

        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entrega_id: entrega._id,
            conductor: conductor,
            fecha_despacho: new Date().toISOString(),
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Error al marcar entrega');
        }

        const result = await response.json();
        results.push(result);
      } catch (error) {
        console.error(`Error al marcar entrega ${entrega._id}:`, error);
        errors.push({
          entrega_id: entrega._id,
          error: error.message,
        });
      }
    }

    return {
      success: errors.length === 0,
      results,
      errors,
      totalProcesadas: results.length,
      totalErrores: errors.length,
    };
  } catch (error) {
    console.error('Error en marcarEntregasEnCamino:', error);
    throw error;
  }
};

/**
 * Marcar una sola entrega como "en camino"
 */
export const marcarEntregaEnCamino = async (entrega, conductor) => {
  try {
    let endpoint;
    if (entrega.tipo === 'lenceria' && entrega.logistica === 'vertical') {
      endpoint = `${API_URL}/entregas-lenceria-vertical/marcar-en-camino`;
    } else if (entrega.tipo === 'lenceria') {
      endpoint = `${API_URL}/entregas-lenceria/marcar-en-camino`;
    } else {
      endpoint = `${API_URL}/entregas-insumos/marcar-en-camino`;
    }

    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entrega_id: entrega._id,
        conductor: conductor,
        fecha_despacho: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al marcar entrega en camino');
    }

    return await response.json();
  } catch (error) {
    console.error('Error en marcarEntregaEnCamino:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería en camino
 */
export const fetchEntregasLenceriaEnCamino = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-lenceria/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'en_camino',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería en camino');
    }

    const result = await response.json();
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaEnCamino:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería vertical en camino
 */
export const fetchEntregasLenceriaVerticalEnCamino = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-lenceria-vertical/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'en_camino',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería vertical en camino');
    }

    const result = await response.json();
    
    // El backend puede devolver en diferentes formatos
    let entregas = [];
    let total = 0;
    
    if (Array.isArray(result)) {
      entregas = result;
      total = result.length;
    } else if (result.data) {
      if (Array.isArray(result.data)) {
        entregas = result.data;
        total = result.total || result.data.length;
      } else {
        entregas = result.data.entregas || [];
        total = result.data.total || result.total || entregas.length;
      }
    } else if (result.entregas) {
      entregas = result.entregas;
      total = result.total || result.totalRecords || entregas.length;
    }
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaVerticalEnCamino:', error);
    throw error;
  }
};

/**
 * Obtener entregas de insumos en camino
 */
export const fetchEntregasInsumosEnCamino = async () => {
  try {
    const response = await fetch(`${API_URL}/entregas-insumos/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'en_camino',
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de insumos en camino');
    }

    const result = await response.json();
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasInsumosEnCamino:', error);
    throw error;
  }
};

/**
 * Obtener todas las entregas en camino (lencería horizontal + vertical + insumos)
 */
export const fetchEntregasEnCamino = async () => {
  try {
    // Obtener entregas de lencería horizontal
    const lenceriaResponse = await fetchEntregasLenceriaEnCamino();

    // Obtener entregas de lencería vertical
    const lenceriaVerticalResponse = await fetchEntregasLenceriaVerticalEnCamino();

    // Obtener entregas de insumos
    const insumosResponse = await fetchEntregasInsumosEnCamino();

    // Agregar tipo a cada entrega
    const lenceriaConTipo = (lenceriaResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'horizontal'
    }));

    const lenceriaVerticalConTipo = (lenceriaVerticalResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'vertical'
    }));

    const insumosConTipo = (insumosResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'insumos',
    }));

    // Combinar todas las listas
    const todasEntregas = [...lenceriaConTipo, ...lenceriaVerticalConTipo, ...insumosConTipo];

    // Ordenar por fecha de despacho (más recientes primero)
    todasEntregas.sort((a, b) => {
      return new Date(a.fecha_despacho || a.createdAt) - new Date(b.fecha_despacho || b.createdAt);
    });

    return {
      entregas: todasEntregas,
      total: todasEntregas.length,
      totalLenceria: lenceriaConTipo.length,
      totalInsumos: insumosConTipo.length,
    };
  } catch (error) {
    console.error('Error en fetchEntregasEnCamino:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería completadas del día
 */
export const fetchEntregasLenceriaCompletadas = async () => {
  try {
    // Obtener fecha de inicio y fin del día
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);

    const response = await fetch(`${API_URL}/entregas-lenceria/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'entregado',
        fecha_entrega_desde: hoy.toISOString(),
        fecha_entrega_hasta: manana.toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería completadas');
    }

    const result = await response.json();
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaCompletadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería vertical completadas del día
 */
export const fetchEntregasLenceriaVerticalCompletadas = async () => {
  try {
    // Obtener fecha de inicio y fin del día
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);

    const response = await fetch(`${API_URL}/entregas-lenceria-vertical/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'entregado',
        fecha_entrega_desde: hoy.toISOString(),
        fecha_entrega_hasta: manana.toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería vertical completadas');
    }

    const result = await response.json();
    
    // El backend puede devolver en diferentes formatos
    let entregas = [];
    let total = 0;
    
    if (Array.isArray(result)) {
      entregas = result;
      total = result.length;
    } else if (result.data) {
      if (Array.isArray(result.data)) {
        entregas = result.data;
        total = result.total || result.data.length;
      } else {
        entregas = result.data.entregas || [];
        total = result.data.total || result.total || entregas.length;
      }
    } else if (result.entregas) {
      entregas = result.entregas;
      total = result.total || result.totalRecords || entregas.length;
    }
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaVerticalCompletadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de insumos completadas del día
 */
export const fetchEntregasInsumosCompletadas = async () => {
  try {
    // Obtener fecha de inicio y fin del día
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);

    const response = await fetch(`${API_URL}/entregas-insumos/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        page: 1,
        limit: 100,
        estado: 'entregado',
        fecha_entrega_desde: hoy.toISOString(),
        fecha_entrega_hasta: manana.toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de insumos completadas');
    }

    const result = await response.json();
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
    
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasInsumosCompletadas:', error);
    throw error;
  }
};

/**
 * Obtener todas las entregas completadas del día (lencería horizontal + vertical + insumos)
 */
export const fetchEntregasCompletadas = async () => {
  try {
    // Obtener entregas de lencería horizontal
    const lenceriaResponse = await fetchEntregasLenceriaCompletadas();

    // Obtener entregas de lencería vertical
    const lenceriaVerticalResponse = await fetchEntregasLenceriaVerticalCompletadas();

    // Obtener entregas de insumos
    const insumosResponse = await fetchEntregasInsumosCompletadas();

    // Agregar tipo a cada entrega
    const lenceriaConTipo = (lenceriaResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'horizontal'
    }));

    const lenceriaVerticalConTipo = (lenceriaVerticalResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'vertical'
    }));

    const insumosConTipo = (insumosResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'insumos',
    }));

    // Combinar todas las listas
    const todasEntregas = [...lenceriaConTipo, ...lenceriaVerticalConTipo, ...insumosConTipo];

    // Ordenar por fecha de entrega (más recientes primero)
    todasEntregas.sort((a, b) => {
      return new Date(b.fecha_entrega || b.createdAt) - new Date(a.fecha_entrega || a.createdAt);
    });

    return {
      entregas: todasEntregas,
      total: todasEntregas.length,
      totalLenceria: lenceriaConTipo.length,
      totalInsumos: insumosConTipo.length,
    };
  } catch (error) {
    console.error('Error en fetchEntregasCompletadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería recolectadas del día
 */
export const fetchEntregasLenceriaRecolectadas = async () => {
  try {
    // Obtener fecha de inicio y fin del día
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);

    const filtros = {
      page: 1,
      limit: 100,
      estado: 'recoleccion',
      createdAt_desde: hoy.toISOString(),
      createdAt_hasta: manana.toISOString(),
    };


    const response = await fetch(`${API_URL}/entregas-lenceria/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería recolectadas');
    }

    const result = await response.json();
    
    const entregas = result.data?.entregas || result.entregas || [];
    const total = result.data?.total || result.total || result.totalRecords || 0;
        
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaRecolectadas:', error);
    throw error;
  }
};

/**
 * Obtener entregas de lencería vertical recolectadas del día
 */
export const fetchEntregasLenceriaVerticalRecolectadas = async () => {
  try {
    // Obtener fecha de inicio y fin del día
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const manana = new Date(hoy);
    manana.setDate(manana.getDate() + 1);

    const filtros = {
      page: 1,
      limit: 100,
      estado: 'recoleccion',
      createdAt_desde: hoy.toISOString(),
      createdAt_hasta: manana.toISOString(),
    };

    const response = await fetch(`${API_URL}/entregas-lenceria-vertical/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filtros),
    });

    if (!response.ok) {
      throw new Error('Error al obtener entregas de lencería vertical recolectadas');
    }

    const result = await response.json();
    
    // El backend puede devolver en diferentes formatos
    let entregas = [];
    let total = 0;
    
    if (Array.isArray(result)) {
      entregas = result;
      total = result.length;
    } else if (result.data) {
      if (Array.isArray(result.data)) {
        entregas = result.data;
        total = result.total || result.data.length;
      } else {
        entregas = result.data.entregas || result.data.data || [];
        total = result.data.total || result.total || entregas.length;
      }
    } else if (result.entregas) {
      entregas = result.entregas;
      total = result.total || result.totalRecords || entregas.length;
    }
        
    return {
      entregas,
      total,
    };
  } catch (error) {
    console.error('Error en fetchEntregasLenceriaVerticalRecolectadas:', error);
    throw error;
  }
};

/**
 * Obtener todas las entregas recolectadas del día (lencería horizontal + vertical)
 */
export const fetchEntregasRecolectadas = async () => {
  try {
    // Obtener entregas de lencería horizontal
    const lenceriaResponse = await fetchEntregasLenceriaRecolectadas();

    // Obtener entregas de lencería vertical
    const lenceriaVerticalResponse = await fetchEntregasLenceriaVerticalRecolectadas();

    // Agregar tipo a cada entrega
    const lenceriaConTipo = (lenceriaResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'horizontal'
    }));

    const lenceriaVerticalConTipo = (lenceriaVerticalResponse.entregas || []).map(e => ({
      ...e,
      tipo: 'lenceria',
      logistica: 'vertical'
    }));

    // Combinar todas las listas
    const todasEntregas = [...lenceriaConTipo, ...lenceriaVerticalConTipo];

    // Ordenar por fecha de creación (más recientes primero)
    todasEntregas.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return {
      entregas: todasEntregas,
      total: todasEntregas.length,
      totalLenceria: lenceriaConTipo.length,
      totalLenceriaVertical: lenceriaVerticalConTipo.length,
    };
  } catch (error) {
    console.error('Error en fetchEntregasRecolectadas:', error);
    throw error;
  }
};
